import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { api } from './client';
import type {
  ActionItem, ActionWrite, Board, BoardFilters, Contact, ContactWrite, Customer, CustomerSummary,
  CustomerWrite, HistoricalOpportunity, HistoricalQuery, Meta, MyWork, Opportunity,
  OpportunityWrite, PagedResult, Partner, PartnerWrite, SearchResult, StageHistoryEntry,
  StageMove, User,
} from './types';

const V1 = '/api/v1';

export const keys = {
  meta: ['meta'] as const,
  board: (filters?: BoardFilters) =>
    ['board', filters?.ownerUserId ?? '', filters?.partnerId ?? ''] as const,
  historical: (query: HistoricalQuery) =>
    ['historical', query.outcome ?? '', query.q ?? '', query.page] as const,
  historicalCount: ['historical', 'count'] as const,
  partners: (q?: string) => ['partners', q ?? ''] as const,
  users: ['users'] as const,
  myOpenOpportunities: (userId: string) => ['opportunities', 'mine-open', userId] as const,
  customers: (q?: string) => ['customers', q ?? ''] as const,
  customer: (id: number) => ['customer', id] as const,
  customerContacts: (id: number) => ['customer', id, 'contacts'] as const,
  customerOpportunities: (id: number) => ['customer', id, 'opportunities'] as const,
  customerActions: (id: number) => ['customer', id, 'actions'] as const,
  opportunity: (id: number) => ['opportunity', id] as const,
  opportunityHistory: (id: number) => ['opportunity', id, 'history'] as const,
  opportunityActions: (id: number) => ['opportunity', id, 'actions'] as const,
  myWork: (filter: string) => ['my-work', filter] as const,
  search: (q: string) => ['search', q] as const,
};

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}

// ── Reads ────────────────────────────────────────────────────────────────────

export const useMeta = (): UseQueryResult<Meta> =>
  useQuery({ queryKey: keys.meta, queryFn: () => get<Meta>(`${V1}/meta`), staleTime: 60 * 60 * 1000 });

export const useBoard = (filters?: BoardFilters): UseQueryResult<Board> =>
  useQuery({
    queryKey: keys.board(filters),
    queryFn: () => get<Board>(`${V1}/opportunities/board`, {
      ownerUserId: filters?.ownerUserId || undefined,
      partnerId: filters?.partnerId ?? undefined,
    }),
  });

/** Lazy on purpose: Historical is an archive, so it is only fetched once the section is opened.
 *  The collapsed header still needs a count, which `useHistoricalCount` fetches with pageSize=1. */
export const useHistoricalOpportunities = (query: HistoricalQuery, enabled: boolean) =>
  useQuery({
    queryKey: keys.historical(query),
    queryFn: () => get<PagedResult<HistoricalOpportunity>>(`${V1}/opportunities/historical`, {
      outcome: query.outcome, q: query.q || undefined, page: query.page, pageSize: 25,
    }),
    enabled,
    placeholderData: (previous) => previous,
  });

export const useHistoricalCount = () =>
  useQuery({
    queryKey: keys.historicalCount,
    queryFn: async () => {
      const result = await get<PagedResult<HistoricalOpportunity>>(
        `${V1}/opportunities/historical`, { page: 1, pageSize: 1 });
      return result.totalCount;
    },
  });

export const usePartners = (q?: string) =>
  useQuery({
    queryKey: keys.partners(q),
    queryFn: () => get<PagedResult<Partner>>(`${V1}/partners`, { q: q || undefined, pageSize: 100 }),
  });

export const useUsers = () =>
  useQuery({
    queryKey: keys.users,
    queryFn: async () => (await get<{ items: User[] }>(`${V1}/users`)).items,
    staleTime: 5 * 60 * 1000,
  });

/** Feeds the My Actions opportunity picker. Scoped to the caller's own open deals (ADR-75). */
export const useMyOpenOpportunities = (userId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: keys.myOpenOpportunities(userId ?? ''),
    queryFn: () => get<PagedResult<Opportunity>>(`${V1}/opportunities`, {
      ownerUserId: userId, openOnly: true, pageSize: 100, sort: '-updatedAtUtc',
    }),
    enabled: enabled && Boolean(userId),
  });

export const useCustomers = (q?: string) =>
  useQuery({
    queryKey: keys.customers(q),
    queryFn: () => get<PagedResult<CustomerSummary>>(`${V1}/customers`, { q: q || undefined, pageSize: 100 }),
  });

export const useCustomer = (id: number) =>
  useQuery({ queryKey: keys.customer(id), queryFn: () => get<Customer>(`${V1}/customers/${id}`), enabled: id > 0 });

export const useCustomerContacts = (id: number) =>
  useQuery({ queryKey: keys.customerContacts(id), queryFn: () => get<Contact[]>(`${V1}/customers/${id}/contacts`), enabled: id > 0 });

export const useCustomerOpportunities = (id: number) =>
  useQuery({ queryKey: keys.customerOpportunities(id), queryFn: () => get<Opportunity[]>(`${V1}/customers/${id}/opportunities`), enabled: id > 0 });

export const useCustomerActions = (id: number) =>
  useQuery({
    queryKey: keys.customerActions(id),
    queryFn: () => get<PagedResult<ActionItem>>(`${V1}/customers/${id}/actions`, { pageSize: 100 }),
    enabled: id > 0,
  });

export const useOpportunity = (id: number) =>
  useQuery({ queryKey: keys.opportunity(id), queryFn: () => get<Opportunity>(`${V1}/opportunities/${id}`), enabled: id > 0 });

export const useOpportunityActions = (id: number) =>
  useQuery({
    queryKey: keys.opportunityActions(id),
    queryFn: () => get<PagedResult<ActionItem>>(`${V1}/opportunities/${id}/actions`, { pageSize: 100 }),
    enabled: id > 0,
  });

export const useOpportunityHistory = (id: number) =>
  useQuery({ queryKey: keys.opportunityHistory(id), queryFn: () => get<StageHistoryEntry[]>(`${V1}/opportunities/${id}/history`), enabled: id > 0 });

export const useMyWork = (filter: string) =>
  useQuery({ queryKey: keys.myWork(filter), queryFn: () => get<MyWork>(`${V1}/actions/my`, { filter }) });

export const useSearch = (q: string) =>
  useQuery({
    queryKey: keys.search(q),
    queryFn: () => get<SearchResult>(`${V1}/search`, { q }),
    enabled: q.trim().length > 0,
  });

// ── Writes ───────────────────────────────────────────────────────────────────

/** Everything is cross-linked (a new action changes the board, the customer and the
 *  opportunity), so mutations invalidate broadly rather than trying to patch caches. */
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => {
    // Everything is cross-linked, but `meta` is static config with a long staleTime — refetching
    // it on every write is pure noise.
    void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'meta' });
  };
}

export function useSaveCustomer() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: number; body: CustomerWrite }) => {
      const { data } = id
        ? await api.put<Customer>(`${V1}/customers/${id}`, body)
        : await api.post<Customer>(`${V1}/customers`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteCustomer() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${V1}/customers/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveContact() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: number; body: ContactWrite }) => {
      const { data } = id
        ? await api.put<Contact>(`${V1}/contacts/${id}`, body)
        : await api.post<Contact>(`${V1}/contacts`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${V1}/contacts/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveOpportunity() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: number; body: OpportunityWrite }) => {
      const { data } = id
        ? await api.put<Opportunity>(`${V1}/opportunities/${id}`, body)
        : await api.post<Opportunity>(`${V1}/opportunities`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteOpportunity() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${V1}/opportunities/${id}`),
    onSuccess: invalidate,
  });
}

export function useMoveStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, move }: { id: number; move: StageMove }) => {
      const { data } = await api.post<Opportunity>(`${V1}/opportunities/${id}/stage`, move);
      return data;
    },
    // Optimistically move the card so the board never feels laggy; roll back on failure.
    onMutate: async ({ id, move }) => {
      // The board key now carries its filters, so patch whichever board is currently cached.
      await qc.cancelQueries({ queryKey: ['board'] });
      const entry = qc.getQueriesData<Board>({ queryKey: ['board'] })
        .find(([, board]) => board !== undefined);
      const boardKey = entry?.[0];
      const previous = entry?.[1];
      if (previous && boardKey) {
        const card = previous.columns.flatMap((c) => c.cards).find((c) => c.id === id);
        if (card) {
          qc.setQueryData<Board>(boardKey, {
            columns: previous.columns.map((column) => {
              const without = column.cards.filter((c) => c.id !== id);
              if (column.stage !== move.toStage) return { ...column, cards: without };
              return {
                ...column,
                cards: [{ ...card, closedOutcome: move.closedOutcome }, ...without],
              };
            }),
          });
        }
      }
      return { previous, boardKey };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous && context.boardKey) qc.setQueryData(context.boardKey, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'meta' });
    },
  });
}

export function useSaveAction() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: number; body: ActionWrite }) => {
      const { data } = id
        ? await api.put<ActionItem>(`${V1}/actions/${id}`, body)
        : await api.post<ActionItem>(`${V1}/actions`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useCompleteAction() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, outcome }: { id: number; outcome: string | null }) => {
      const { data } = await api.post<ActionItem>(`${V1}/actions/${id}/complete`, { outcome });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useSavePartner() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: number; body: PartnerWrite }) => {
      const { data } = id
        ? await api.put<Partner>(`${V1}/partners/${id}`, body)
        : await api.post<Partner>(`${V1}/partners`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeletePartner() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${V1}/partners/${id}`),
    onSuccess: invalidate,
  });
}

export function useChangeOpportunityOwner() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, ownerUserId }: { id: number; ownerUserId: string | null }) => {
      const { data } = await api.post<Opportunity>(`${V1}/opportunities/${id}/owner`, { ownerUserId });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useChangeCustomerOwner() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async ({ id, ownerUserId }: { id: number; ownerUserId: string | null }) => {
      const { data } = await api.post<Customer>(`${V1}/customers/${id}/owner`, { ownerUserId });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAction() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${V1}/actions/${id}`),
    onSuccess: invalidate,
  });
}
